import { elakayAutoText } from "../pageInside/dataElakay";
import { buildGradientOrSolidBackground } from "../../config";

// Valeur par defaut utilisee tant que les reglages ne sont pas encore
// charges depuis la base de donnees (AnimationSettings.dropshippingOut),
// ou si aucun reglage n'existe encore : le service et toutes les
// plateformes restent actifs.
const DEFAULT_SERVICE_ACTIVE = true;
const DEFAULT_PLATFORM_ACTIVE = true;
const DEFAULT_PLATFORM_MODE = "live";

// Normalise la valeur brute stockee dans
// AnimationSettings.dropshippingOut.platformModes.<platformId> ("test" ou
// non configure) vers exactement l'une des deux valeurs attendues par la
// banniere d'environnement et les apps Dropshipping Out : "test" ou "live".
export const normalizeDropshippingPlatformMode = (rawMode) =>
  rawMode === "test" ? "test" : DEFAULT_PLATFORM_MODE;

// Interrupteur global du service Dropshipping Out, pilote depuis la page
// admin AnimationSettings et persiste en base (AnimationSettings.dropshippingOut.enabled).
// `false` met en pause TOUTES les plateformes (affiche "bientot disponible"
// partout ou le service apparait : Sell Center + cette page).
export const isDropshippingServiceActive = (dropshippingSettings) =>
  dropshippingSettings?.enabled ?? DEFAULT_SERVICE_ACTIVE;

// Fond de la page elakay_Dropshipping_out, pilote depuis AnimationSettings et
// persiste en base (AnimationSettings.dropshippingOut.backgroundColor /
// backgroundColorSecondary). Rien de configure => undefined => le CSS natif
// (blanc) de la page s'applique. Une seule couleur renseignee => couleur
// unie. Les deux => degrade vertical.
export const getDropshippingOutBackgroundStyle = (dropshippingSettings) =>
  buildGradientOrSolidBackground(
    dropshippingSettings?.backgroundColor,
    dropshippingSettings?.backgroundColorSecondary,
    180
  );

// Registre unique des plateformes Dropshipping Out.
// Les prochaines integrations s'ajoutent ici et apparaissent automatiquement
// dans elakay_Dropshipping_out sans modifier le composant de page.
// L'etat actif/inactif de chaque plateforme vient de la base de donnees
// (AnimationSettings.dropshippingOut.platforms.<id>), reglable depuis
// AnimationSettings ; `undefined` (pas encore configure) vaut "actif".
export const getDropshippingOutPlatforms = (dropshippingSettings) => {
  const platformFlags = dropshippingSettings?.platforms || {};
  const withActive = (id, platform) => ({
    ...platform,
    id,
    active: platformFlags[id] ?? DEFAULT_PLATFORM_ACTIVE,
  });

  return [
    withActive("cj-dropshipping", {
      name: "CJ Dropshipping",
      logoText: "CJ",
      route: "/dropshipping/cj",
      status: "available",
      description: elakayAutoText(
        "h_cj_out_cj_description",
        "Connectez votre compte CJ, recherchez des produits et importez-les directement dans votre boutique."
      ),
      features: [
        {
          icon: "catalog",
          text: elakayAutoText("h_cj_out_catalog", "Explorer le catalogue CJ"),
        },
        {
          icon: "sync",
          text: elakayAutoText("h_cj_out_sync", "Synchroniser le stock et les prix"),
        },
        {
          icon: "shipping",
          text: elakayAutoText("h_cj_out_orders", "Transmettre et suivre les commandes"),
        },
      ],
      categories: [
        elakayAutoText("h_cj_out_cat_1", "Mode & accessoires"),
        elakayAutoText("h_cj_out_cat_2", "Électronique"),
        elakayAutoText("h_cj_out_cat_3", "Maison & cuisine"),
        elakayAutoText("h_cj_out_cat_4", "Beauté & soins"),
        elakayAutoText("h_cj_out_cat_5", "Sport & plein air"),
        elakayAutoText("h_cj_out_cat_6", "Jouets & enfants"),
      ],
    }),
    withActive("printful", {
      name: "Printful",
      logoText: "PF",
      route: "/dropshipping/printful",
      status: "available",
      description: elakayAutoText(
        "h_pf_out_description",
        "Connectez votre store Printful, importez vos produits personnalisés et automatisez leur fabrication et leur expédition."
      ),
      features: [
        { icon: "catalog", text: elakayAutoText("h_pf_out_catalog", "Synchroniser les produits du store Printful") },
        { icon: "sync", text: elakayAutoText("h_pf_out_import", "Importer les variantes dans votre boutique") },
        { icon: "shipping", text: elakayAutoText("h_pf_out_orders", "Automatiser la fabrication et le tracking") },
      ],
      categories: [
        elakayAutoText("h_pf_out_cat_1", "Vêtements personnalisés"),
        elakayAutoText("h_pf_out_cat_2", "Accessoires imprimés"),
        elakayAutoText("h_pf_out_cat_3", "Décoration murale"),
        elakayAutoText("h_pf_out_cat_4", "Objets pour la maison"),
        elakayAutoText("h_pf_out_cat_5", "Accessoires téléphone"),
      ],
    }),
    withActive("syncee", {
      name: "Syncee",
      logoText: "SY",
      route: "/dropshipping/syncee",
      status: "available",
      description: elakayAutoText(
        "h_syncee_out_description",
        "Connectez Syncee pour découvrir des fournisseurs vérifiés, importer leurs produits et synchroniser votre catalogue."
      ),
      features: [
        { icon: "catalog", text: elakayAutoText("h_syncee_out_catalog", "Explorer les fournisseurs et produits Syncee") },
        { icon: "sync", text: elakayAutoText("h_syncee_out_import", "Importer et synchroniser prix, images et stock") },
        { icon: "shipping", text: elakayAutoText("h_syncee_out_orders", "Centraliser les commandes et leur suivi") },
      ],
      categories: [
        elakayAutoText("h_syncee_out_cat_1", "Mode & bijoux"),
        elakayAutoText("h_syncee_out_cat_2", "Maison & jardin"),
        elakayAutoText("h_syncee_out_cat_3", "Beauté & bien-être"),
        elakayAutoText("h_syncee_out_cat_4", "Électronique"),
        elakayAutoText("h_syncee_out_cat_5", "Animalerie"),
        elakayAutoText("h_syncee_out_cat_6", "Alimentation & boissons"),
      ],
    }),
    withActive("printify", {
      name: "Printify",
      logoText: "PY",
      route: "/dropshipping/printify",
      status: "available",
      description: elakayAutoText(
        "h_printify_out_description",
        "Connectez Printify par OAuth, importez vos produits personnalisés et automatisez leur production et leur livraison."
      ),
      features: [
        { icon: "catalog", text: elakayAutoText("h_printify_out_catalog", "Synchroniser tous les shops et produits Printify") },
        { icon: "sync", text: elakayAutoText("h_printify_out_import", "Importer les variantes, mockups, prix et SKU") },
        { icon: "shipping", text: elakayAutoText("h_printify_out_orders", "Automatiser les commandes, webhooks et tracking") },
      ],
      categories: [
        elakayAutoText("h_printify_out_cat_1", "Vêtements personnalisés"),
        elakayAutoText("h_printify_out_cat_2", "Mugs & objets imprimés"),
        elakayAutoText("h_printify_out_cat_3", "Décoration"),
        elakayAutoText("h_printify_out_cat_4", "Accessoires"),
        elakayAutoText("h_printify_out_cat_5", "Papeterie"),
      ],
    }),
    withActive("inventory-source", {
      name: "Inventory Source",
      logoText: "IS",
      route: "/dropshipping/inventory-source",
      status: "available",
      description: elakayAutoText(
        "h_inventorySource_out_description",
        "Connectez votre propre compte Inventory Source ou Flxpoint et automatisez catalogue, stock, prix et commandes."
      ),
      features: [
        { icon: "catalog", text: elakayAutoText("h_inventorySource_out_catalog", "Synchroniser vos fournisseurs et leurs produits") },
        { icon: "sync", text: elakayAutoText("h_inventorySource_out_import", "Importer les produits dans votre boutique Elakay") },
        { icon: "shipping", text: elakayAutoText("h_inventorySource_out_orders", "Router et suivre les commandes fournisseur") },
      ],
      categories: [
        elakayAutoText("h_inventorySource_out_cat_1", "Multi-fournisseurs"),
        elakayAutoText("h_inventorySource_out_cat_2", "Électronique"),
        elakayAutoText("h_inventorySource_out_cat_3", "Maison & jardin"),
        elakayAutoText("h_inventorySource_out_cat_4", "Automobile"),
        elakayAutoText("h_inventorySource_out_cat_5", "Santé & beauté"),
        elakayAutoText("h_inventorySource_out_cat_6", "Sport"),
      ],
    }),
  ];
};

export default getDropshippingOutPlatforms;
